/*
  Сверка скана - макет Г, одобренный владельцем 30.09.2026 вместе с вариантом Б.

  Слева картинка страницы, справа поля, которые распознавание заполнило.
  Жёлтым - всё, что прочитано с картинки (с 06.10.2026 это все графы бланка
  каждого жильца, а счёт страницы без строки над сканом - подобранный по
  адресу): одна неверная цифра паспорта - заявление к постороннему человеку
  (строка 177 реестра компромиссов). Что не сходится (квартира в графе и в
  адресе, паспорт раньше рождения), сервер называет пояснением над полями -
  по строке на каждое. В накопитель страница уходит только кнопкой «Верно,
  дальше», а пропустить её можно всегда.

  Три правки проверки перед выкладкой (01.10.2026):
  - несохранённые правки не выбрасываются молча: «Назад», «Дальше» и «Закрыть»
    при правках спрашивают (`UnsavedConfirm`), а пока страница уходит на
    сервер, листать нельзя;
  - открытый блок сам встаёт перед глазами: человек нажимает «Сверить» внизу
    таблицы, а блок появляется над ней. Прокрутка к заголовку и фокус на нём -
    читалка экрана тоже называет, что открылось;
  - пока идёт перенос, решение по странице выключено: сервер отказал бы (409).

  С 19.10.2026 (решение владельца, макет «2-сверить-все-А») в шапке рядом с
  «Закрыть» - «Сверить все»: подтверждает разом ждущие страницы, которые сервер
  принял бы и по одной, остальные оставляет на сверке (`ScanConfirmAll.jsx`).
  Несохранённые правки текущей страницы она не подхватывает - поэтому при них
  сначала тот же вопрос, что у «Дальше».
*/

import { useEffect, useRef, useState } from 'react'

import { api } from '../../api/client.js'
import { Lock } from '../AccessContext.jsx'
import { Confirm, Field, Loading, Notice } from '../Common.jsx'
import { ConfirmAllDialog, ConfirmAllResult } from './ScanConfirmAll.jsx'
import ScanPeople from './ScanPeople.jsx'
import ScanPerson from './ScanPerson.jsx'
import { FIELD_LIMITS } from './scanFields.js'
import TransferLock from './TransferLock.jsx'
import useConfirmAll from './useConfirmAll.js'
import useScanReview from './useScanReview.js'

// Окно о несохранённых правках - одно на сверку и на смену файла во вкладке.
export function UnsavedConfirm({ onLeave, onStay }) {
  return (
    <Confirm title="Уйти без сохранения?" action="Уйти без сохранения" onConfirm={onLeave} onCancel={onStay}>
      На этой странице скана есть поправленные поля, и они ещё не сохранены. Чтобы сохранить их,
      нажмите «Отмена», а затем «Верно, дальше».
    </Confirm>
  )
}

// Плавно - только если человек не просил систему убирать движение.
function bringIntoView(node) {
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  node.scrollIntoView?.({ behavior: still ? 'auto' : 'smooth', block: 'start' })
  node.focus({ preventScroll: true })
}

// Общее у страницы - счёт и адрес; графы людей рисует `ScanPerson` (макет Б
// «как в бланке», 01.10.2026).
const PAGE_TITLES = [
  ['account_number', 'Лицевой счёт'],
  ['address', 'Адрес'],
]

const STATE_NOTES = {
  confirmed: 'Страница сверена - сведения в накопителе. Можно поправить и подтвердить снова.',
  skipped: 'Страница пропущена - сведений из неё нет.',
}

function Fields({ review }) {
  const doubtful = new Set(review.page.doubtful)
  return (
    <div className="stack">
      {PAGE_TITLES.map(([name, title]) => (
        <Field key={name} label={title} className={doubtful.has(name) ? 'field--doubtful' : undefined}>
          <input
            className="field__input"
            maxLength={FIELD_LIMITS[name]}
            value={review.values[name] ?? ''}
            onChange={(event) => review.edit(name, event.target.value)}
          />
        </Field>
      ))}
      <fieldset className="scan__person">
        <legend>Человек 1 - должник</legend>
        <ScanPerson values={review.values} doubtful={doubtful} onEdit={review.edit} />
      </fieldset>
      <ScanPeople people={review.people} />
      <p className="muted">
        Жёлтым - то, в чём распознавание не уверено: сверьте с картинкой страницы. Графа на
        бланке пустая или не читается - оставьте поле пустым.
      </p>
    </div>
  )
}

export default function ScanReview({ file, onDecided, onClose, onDirty, locked = false }) {
  const review = useScanReview(file.id, onDecided)
  const bulk = useConfirmAll(file.id, async () => {
    await review.reload()
    onDecided?.()
  })
  const heading = useRef(null)
  const [leaving, setLeaving] = useState(null)
  const opened = Boolean(review.pages)

  useEffect(() => {
    if (opened && heading.current) bringIntoView(heading.current)
  }, [opened])

  // Вкладке нужно знать о правках, чтобы спросить и при выборе другого файла.
  useEffect(() => {
    onDirty?.(review.dirty)
  }, [review.dirty, onDirty])
  useEffect(() => () => onDirty?.(false), [onDirty])

  if (!review.pages) {
    return review.work.error ? <Notice error={review.work.error} /> : <Loading what="Загружаем страницы скана" />
  }
  const { page, pages, index, work } = review
  const waiting = new Set(pages.filter((item) => item.state === 'waiting').map((item) => item.number))
  const left = waiting.size
  // После «Сверить все» все оставшиеся - те, что программа подтвердить не смогла.
  const exhausted = Boolean(bulk.result) && left <= bulk.result.left.length
  /*
    Пояснение сервера бывает у двух разных страниц (01.10.2026). Пропущенная
    сама (текстовая, не скан выписки) - сверять нечего, и полей нет. Ждущая
    сверки (распознавание не уложилось во время, поля пустые) - пояснение
    стоит над полями, а вписать и подтвердить их можно: раньше такая страница
    теряла и поля, и кнопки, и сверить её было нельзя вовсе.
  */
  const closed = Boolean(page.note) && page.state !== 'waiting'
  // Без правок уходим сразу, с правками - после вопроса.
  const leave = (run) => (review.dirty ? setLeaving(() => run) : run())
  return (
    <section className="section card">
      <div className="row row--between">
        <h2 ref={heading} tabIndex={-1} className="section__title filename">
          Сверка скана «{file.filename}» · страница {index + 1} из {pages.length}
        </h2>
        <div className="row row--tight scan__head-actions">
          <Lock>
            <button
              type="button"
              className="button button--quiet"
              disabled={work.busy || bulk.busy || locked || left === 0 || exhausted}
              title={exhausted ? 'Оставшиеся страницы без сверки подтвердить нельзя: в них не хватает сведений' : undefined}
              onClick={() => leave(bulk.ask)}
            >
              Сверить все
            </button>
          </Lock>
          <button type="button" className="button button--quiet" onClick={() => leave(onClose)}>
            Закрыть
          </button>
        </div>
      </div>
      <Notice error={bulk.askError} />
      <p className="muted">Ждут сверки: {left}</p>
      <ConfirmAllResult result={bulk.result} waiting={waiting} onGo={(number) => leave(() => review.show(number))} />
      <div className="scan">
        <div className="stack">
          <img
            className="scan__page"
            src={api.scanPageImage(file.id, page.number)}
            alt={`Страница ${page.number} скана`}
          />
          {/* Цифры паспорта в колонке мелкие - сверять их надо в полный размер
              (осмотр 30.09.2026). Ссылка, а не кнопка: открывается рядом. */}
          <a href={api.scanPageImage(file.id, page.number)} target="_blank" rel="noreferrer">
            Открыть страницу крупно
          </a>
        </div>
        <div>
          {page.note ? (
            <Notice kind="warn">
              {page.note.split('\n').map((line) => (
                <p key={line}>{line}</p>
              ))}
            </Notice>
          ) : null}
          {closed ? null : <Fields review={review} />}
          {STATE_NOTES[page.state] ? <p className="muted">{STATE_NOTES[page.state]}</p> : null}
        </div>
      </div>
      <div className="row row--between">
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={work.busy || index === 0}
            onClick={() => leave(() => review.go(-1))}
          >
            Назад
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={work.busy || index === pages.length - 1}
            onClick={() => leave(() => review.go(1))}
          >
            Дальше
          </button>
        </div>
        {closed ? null : (
          <Lock>
            <div className="row">
              <button
                type="button"
                className="button button--quiet"
                disabled={work.busy || locked}
                onClick={() => review.decide(false)}
              >
                Пропустить страницу
              </button>
              <button type="button" className="button" disabled={work.busy || locked} onClick={() => review.decide(true)}>
                Верно, дальше
              </button>
            </div>
          </Lock>
        )}
      </div>
      <TransferLock locked={locked} />
      <Notice error={work.error} />
      <ConfirmAllDialog bulk={bulk} />
      {leaving ? (
        <UnsavedConfirm
          onLeave={() => {
            setLeaving(null)
            leaving()
          }}
          onStay={() => setLeaving(null)}
        />
      ) : null}
    </section>
  )
}
